import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import './Doctors.css';
import CustomDataTable from './CustomDataTable';
import { FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
//import { useLocation } from 'react-router-dom';
import Cal from './Cal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useAuth from '../hooks/useAuth';

// Build default timePref: next 3 upcoming weekdays (Mon-Fri) from today, AM slot
const getDefaultTimePref = () => {
  const today = new Date().getDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const weekdays = [1, 2, 3, 4, 5]; // Mon-Fri
  // Get weekdays that are today or later this week, then wrap to next week
  const upcoming = [];
  for (let offset = 0; offset < 7 && upcoming.length < 3; offset++) {
    const day = (today + offset) % 7;
    if (weekdays.includes(day)) upcoming.push(day);
  }
  const pref = {};
  upcoming.forEach(d => { pref[d] = { AM: true }; });
  return pref;
};

const PostConsultation = ({ patientCard, patientFData }) => {
  const { auth } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [suggestedServicesCopy, setSuggestedServicesCopy] = useState([]);
  const [todayDate] = useState(new Date());
  const [therapists, setTherapists] = useState([]);
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const modSeqs = useRef([]);
  const [appointments, setAppointments] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRowsState, setSelectedRowsState] = useState([]);
const selectedPatientRef = useRef(null);
const [timePref, setTimePref] = useState({});

const [gtpPlans, setGtpPlans] = useState([]);
const [expandedGtpNumbers, setExpandedGtpNumbers] = useState(new Set());
const patientProfileRef = useRef(null);


  useEffect(() => {
  if (!patientCard) return;

  Promise.all([
    api.get(`${process.env.REACT_APP_URL}/patients/profileconsultation/${patientCard}`),
    api.get(`${process.env.REACT_APP_URL}/patients/profiles/${patientCard}`),
  ]).then(([consultRes, profileRes]) => {
      const patient = consultRes.data;
      setSelectedPatient(patient);
      selectedPatientRef.current = patient;
      patientProfileRef.current = profileRes.data;
      setTimePref(patient.timePref || {});
      setRecommendations(patient.recommendations || []);

      // ── Per-GTP processing ──
      const plans = patient.gtpPlans || [];
      const bills = profileRes.data?.bills || profileRes.data?._doc?.bills || [];

      // Build flat list of all billed services with timestamps
      const allBilled = [];
      bills.forEach(b => {
        (b.services || []).forEach(s => {
          allBilled.push({
            name: s.name || '',
            qty: s.qty || s.quantity || 1,
            amount: s.amount || s.rate || 0,
            invoicedate: b.invoicedate || b.createdAt,
            _billing: { invoicedate: b.invoicedate, paid: b.paid, balance: b.balance, total: b.total },
          });
        });
      });

      // Match bills to GTPs by date+time (bills between GTP N and GTP N+1 belong to GTP N)
      const sortedPlans = [...plans].sort((a, b) => new Date(a.date) - new Date(b.date));
      const processedGtps = sortedPlans.map((gtp, idx) => {
        const gtpDate = new Date(gtp.date);
        const nextGtpDate = idx < sortedPlans.length - 1 ? new Date(sortedPlans[idx + 1].date) : new Date('2099-12-31');
        // Bills belonging to this GTP window
        const gtpBills = allBilled.filter(b => {
          const bd = new Date(b.invoicedate);
          return bd >= gtpDate && bd < nextGtpDate;
        });
        // Match suggested services against bills for this GTP (case-insensitive)
        const billedNameQty = {};
        gtpBills.forEach(b => { billedNameQty[(b.name || '').toLowerCase()] = (billedNameQty[(b.name || '').toLowerCase()] || 0) + (Number(b.qty) || 0); });
        const bought = [];
        const unbought = [];
        (gtp.suggestedServices || []).forEach((s, sIdx) => {
          const n = (s.name || '').toLowerCase();
          const sQty = Number(s.qty) || 0;
          const boughtQty = billedNameQty[n] || 0;
          // Unique ID per service per GTP — prevents duplicate _id across GTPs
          // Override _id so CustomDataTable's internal selection logic works correctly
          const _uid = `gtp${gtp.gtpNumber}_${sIdx}`;
          const origId = s._id;
          if (boughtQty >= sQty && sQty > 0) {
            bought.push({ ...s, boughtQty, _uid, _origServiceId: origId });
          } else if (boughtQty > 0 && boughtQty < sQty) {
            bought.push({ ...s, boughtQty, partial: true, _uid, _origServiceId: origId });
            unbought.push({ ...s, qty: sQty - boughtQty, _remainderOf: s.name, _uid, _id: `${_uid}_rem`, _origServiceId: origId, _fromGtp: gtp.gtpNumber });
          } else if (sQty > 0) {
            unbought.push({ ...s, _uid, _id: _uid, _origServiceId: origId, _fromGtp: gtp.gtpNumber });
          }
        });
        return { ...gtp, bought, unbought, gtpBills };
      });
      setGtpPlans(processedGtps);

      // Billing table: show ALL recommended services from root suggestedServices
      // (accumulated across all GTPs). User selects what to bill and can modify qty.
      const rootSS = (patient.suggestedServices || [])
        .filter(s => (Number(s.qty) || 0) > 0)
        .map((s, i) => ({ ...s, _uid: `ss_${i}`, _id: s._id || `ss_${i}` }));

      setSuggestedServices(rootSS);
      setSuggestedServicesCopy(rootSS);
      setSelectedRowsState(rootSS);

      getTherapists();
      getTherapistSuggestions('startDate', patient.startDate || todayDate, patient);
    })
    .catch((err) => console.error('Error fetching consultation data', err));
}, [patientCard]);

  const getTherapists = () => {
    api.get(`${process.env.REACT_APP_URL}/users/therapist`)
      .then((res) => setTherapists(res.data))
      .catch((err) => console.error('Error fetching therapists:', err));
  };

  const getTherapistSuggestions = (key, value, inputPatient = null) => {
    const updated = { ...selectedPatient, ...(inputPatient || {}), [key]: value };
    setSelectedPatient(updated);
    selectedPatientRef.current = updated; // ✅ Keep ref in sync

    // Build time slots only if startDate is present
  if (!updated.startDate) return;
    const service = updated?.suggestedServices?.[0] || {};
    const sequences = service.sequences || [];

    const totalMin = sequences.reduce((sum, s) => sum + ((s.duration || 0) * (s.repetition || 1)), 0);
    const durationInDays = service.qty || 1;
    const requiredMinPerDay = Math.ceil(totalMin / durationInDays);
    const slotDuration = 60;
    //const daysArray = mapDaysPerWeekToArray(updated.daysperweek || 'MTWRF');
     // Build daysAWeek from new format
  const daysArray = Object.entries(updated.timePref || {})
    .filter(([_, val]) => val.AM || val.PM)
    .map(([day]) => parseInt(day, 10));

    const slotsPerDay = Math.ceil(requiredMinPerDay / slotDuration);

    const patternInput = {
      startDate: new Date(updated.startDate || todayDate),
      durationInDays,
      daysAWeek: daysArray,
      slotDurationInMinutes: slotDuration,
      workingHours: { start: 6, end: 18 }
    };

    const appointmentDates = generateAppointmentDates(patternInput);
    console.log('appointment dates = ', appointmentDates);
  };

  const mapDaysPerWeekToArray = (str = '') => {
    const mapping = { M: 1, T: 2, W: 3, R: 4, F: 5, S: 6, U: 0 };
    return str.split('').map(ch => mapping[ch]).filter(Boolean);
  };

  const generateAppointmentDates = ({ startDate, durationInDays, daysAWeek, slotDurationInMinutes, workingHours }) => {
    const dates = [];
    for (let i = 0; i < durationInDays; i++) {
      const current = new Date(startDate);
      current.setDate(current.getDate() + i);
      if (daysAWeek.includes(current.getDay())) {
        const dateStr = current.toISOString().split('T')[0];
        const slots = generateTimeSlotsForDay(dateStr, workingHours.start, workingHours.end, slotDurationInMinutes);
        dates.push({ date: dateStr, timeSlots: slots });
      }
    }
    return { dates };
  };

  const generateTimeSlotsForDay = (dateStr, startHour, endHour, slotDuration) => {
    const slots = [];
    let current = new Date(`${dateStr}T${String(startHour).padStart(2, '0')}:00`);
    const end = new Date(`${dateStr}T${String(endHour).padStart(2, '0')}:00`);
    while (current < end) {
      const slotEnd = new Date(current.getTime() + slotDuration * 60 * 1000);
      if (slotEnd > end) break;
      slots.push({ start: new Date(current), end: slotEnd });
      current = slotEnd;
    }
    return slots;
  };
const updateServicesToBeSent = () => {
  const final = [];

  for (let i = 0; i < selectedRowsState.length; i++) {
    const originalService = selectedRowsState[i];
    const modifiedService = modSeqs.current.find(m => m._uid === originalService._uid);

    const updated = { ...originalService };

    const isPkg = /package/i.test(originalService.name || '') && Number(originalService._masterQty || 0) > 1;
    if (modifiedService && !isPkg) {
      const originalQty = parseInt(originalService.qty || 0, 10);
      const modifiedQty = parseInt(modifiedService.modifiedQtyByPatient || originalQty, 10);

      // ❌ Skip if qty is zero or negative
      if (modifiedQty <= 0) continue;

      updated.qty = modifiedQty;
      updated.modifiedQtyByPatient = modifiedQty - originalQty;
      updated.remarks = `${modifiedService.remarks || ''}${modifiedQty !== originalQty ? ` | Qty adjusted to ${modifiedQty}` : ''}`;
    }

    // Skip services with zero or no qty
    if (!updated.qty || updated.qty <= 0) continue;

    //updated.sstartDate = selectedPatient?.startDate;
    updated.startDate = selectedPatientRef.current?.startDate;

    // Restore original service _id for billing (remove internal tracking fields)
    if (updated._origServiceId) { updated._id = updated._origServiceId; }
    delete updated._origServiceId;
    delete updated._uid;
    delete updated._fromGtp;
    delete updated._remainderOf;

    final.push(updated);
  }

  return final;
};

  const updateTherapist = () => {
    if (!selectedPatient?.therapist || selectedPatient.therapist === 'selectT') {
      alert('Please assign a therapist');
      return;
    }
    api.post(`${process.env.REACT_APP_URL}/patients/profiles/therapist/${selectedPatient.therapist}/patient/${selectedPatient.patientCard}`)
      .then((resp) => {
        alert("Therapist updated successfully");
      })
      .catch((err) => alert(err))
  }

  const updateSeqs = async () => {

    const services = updateServicesToBeSent();

    const mergedPatientData = {
      ...selectedPatientRef.current,
      ...patientFData,
      startDate: selectedPatientRef.current.startDate instanceof Date
        ? selectedPatientRef.current.startDate
        : new Date(selectedPatientRef.current.startDate),
      therapist: selectedPatientRef.current.therapist
        ? selectedPatientRef.current.therapist
        : "veena",
      timePref: selectedPatientRef.current.timePref
        ? selectedPatientRef.current.timePref
        : {}
    };

    // Default time preference: Mon(1), Wed(3), Fri(5) Morning if not set
    if (!mergedPatientData.timePref || Object.keys(mergedPatientData.timePref).length === 0) {
      mergedPatientData.timePref = getDefaultTimePref();
    }

    if (!mergedPatientData.therapist || mergedPatientData.therapist === 'selectT') {
      alert('Please assign a therapist');
      return;
    }
    if (!mergedPatientData.suggestedServices || mergedPatientData.suggestedServices.length == 0) {
      alert('Please add services during Consultation');
      return;
    }

    // Save therapist assignment at BE before navigating to Billing
    try {
      await api.post(`${process.env.REACT_APP_URL}/patients/profiles/therapist/${mergedPatientData.therapist}/patient/${patientCard}`);
    } catch (err) {
      alert('Failed to update therapist: ' + (err?.response?.data?.message || err.message));
      return;
    }

    // Build dets (per-org outstanding breakdown) same as ListPatients.getpreviousOutstandingWithOrgs
    const profile = patientProfileRef.current || patientFData || {};
    const rawDets = (profile.previousbalancedetails || []).map(d => {
      const bal = Number(d.balance || 0);
      return { ...d, filter: bal > 0 ? 'pending' : bal < 0 ? 'refund' : null };
    }).filter(d => d.filter);

    navigate('/Billing', {
      state: {
        statedata: {
          patientCard,
          services,
          dets: rawDets,
          patientCData: mergedPatientData
        }
      }
    });
  };

  if (!patientCard) {
    return <div>Loading or invalid patient...</div>;
  }

  const tableData = {
    columns: [
      { name: "Recommendation", selector: row => row?.recommendation, sortable: true },
      { name: "Quantity", selector: row => row?.qty, sortable: true },
      { name: "No. of Days", selector: row => row?.days, sortable: true },
      { name: "No. Of Time", selector: row => row?.not, sortable: true },
      { name: "StartDate", selector: row => row?.startDate },
      { name: "Remarks", selector: row => row?.remarks }
    ],
    data: recommendations
  };

  const _isPkg = (svc) => /package/i.test(svc.name || '') && Number(svc._masterQty || 0) > 1;

  const sstableData = {
    columns: [
      { name: "Name", selector: row => row?.name, sortable: true },
      { name: "Description", selector: row => row?.description, sortable: true },
      { name: "Amount", selector: row => row?.amount, sortable: true },
      { name: "Suggested Qty", selector: row => row?.qty, sortable: true },
      {
        name: "Qty to Bill",
        cell: (row, idx) => _isPkg(row)
          ? <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>Package (fixed)</span>
          : (
          <input
            type="number"
            min="0"
            value={row.modifiedQtyByPatient || ''}
            onChange={e => {
              const updated = suggestedServices.map(item =>
                item._uid === row._uid ? { ...item, modifiedQtyByPatient: e.target.value } : item
              );
              setSuggestedServices(updated);

              modSeqs.current = [...modSeqs.current.filter(r => r._uid !== row._uid), updated.find(i => i._uid === row._uid)];

              if (!selectedRowsState.some(r => r._uid === row._uid)) {
                setSelectedRowsState([...selectedRowsState, updated.find(i => i._uid === row._uid)]);
              }
            }}
          />
        )
      },
     {
        name: "Remarks",
        cell: (row, idx) => (
          <input
            type="text"
            value={row.remarks || ''}
            onChange={e => {
              const updated = suggestedServices.map(item =>
                item._uid === row._uid ? { ...item, remarks: e.target.value } : item
              );
              setSuggestedServices(updated);

              modSeqs.current = [...modSeqs.current.filter(r => r._uid !== row._uid), updated.find(i => i._uid === row._uid)];

              if (!selectedRowsState.some(r => r._uid === row._uid)) {
                setSelectedRowsState([...selectedRowsState, updated.find(i => i._uid === row._uid)]);
              }
            }}
          />
        )
      }

    ],
    data: suggestedServices
  };

  // Shared card container style
  const card = {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
    marginBottom: 16,
    overflow: 'hidden',
  };

  const cardHeader = (icon, title) => (
    <div style={{
      padding: '12px 18px',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{title}</span>
    </div>
  );

  return (
    <div style={{ padding: '4px 0' }}>

      {/* ── Start Date ── */}
      <div style={card}>
        {cardHeader('📅', 'Start Date')}
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <DatePicker
            selected={selectedPatient?.startDate instanceof Date ? selectedPatient.startDate : todayDate}
            onChange={(date) => getTherapistSuggestions('startDate', date)}
          />
        </div>
      </div>

      {/* ── Time Preferences ── */}
      <div style={card}>
        {cardHeader('🕐', 'Time Preferences (Day-wise)')}
        <div style={{ padding: '12px 18px', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Day</th>
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Morning</th>
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Evening</th>
              </tr>
            </thead>
            <tbody>
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((dayName, dayIdx) => (
                <tr key={dayIdx} style={{ borderBottom: '1px solid #f3f4f6', background: timePref?.[dayIdx]?.AM || timePref?.[dayIdx]?.PM ? '#eff6ff' : '#fff' }}>
                  <td style={{ padding: '7px 14px', fontWeight: timePref?.[dayIdx]?.AM || timePref?.[dayIdx]?.PM ? 600 : 400, color: '#374151' }}>{dayName}</td>
                  <td style={{ padding: '7px 14px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={timePref?.[dayIdx]?.AM || false}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                      onChange={(e) => {
                        const updated = { ...timePref };
  if (!updated[dayIdx]) updated[dayIdx] = {};
  if (e.target.checked) {
    updated[dayIdx].AM = true;
  } else {
    delete updated[dayIdx].AM;
  }
  if (!updated[dayIdx].AM && !updated[dayIdx].PM) {
    delete updated[dayIdx];
  }
                        setTimePref(updated);
                        const newPatient = { ...selectedPatientRef.current, timePref: updated };
                        setSelectedPatient(newPatient);
                        selectedPatientRef.current = newPatient;
                        getTherapistSuggestions('timePref', updated, newPatient);
                      }}
                    />
                  </td>
                  <td style={{ padding: '7px 14px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={timePref?.[dayIdx]?.PM || false}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                      onChange={(e) => {
                        const updated = { ...timePref };
                        if (!updated[dayIdx]) updated[dayIdx] = {};
  if (e.target.checked) {
    updated[dayIdx].PM = true;
  } else {
    delete updated[dayIdx].PM;
  }
  if (!updated[dayIdx].AM && !updated[dayIdx].PM) {
    delete updated[dayIdx];
  }
                        setTimePref(updated);
                        const newPatient = { ...selectedPatientRef.current, timePref: updated };
                        setSelectedPatient(newPatient);
                        selectedPatientRef.current = newPatient;
                        getTherapistSuggestions('timePref', updated, newPatient);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Therapist ── */}
      <div style={card}>
        {cardHeader('👤', 'Assign Therapist')}
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={selectedPatient?.therapist || 'selectT'}
            onChange={(e) => setSelectedPatient({ ...selectedPatient, therapist: e.target.value })}
            style={{
              padding: '9px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 13,
              color: '#1f2937',
              background: '#fafafa',
              minWidth: 180,
              cursor: 'pointer',
            }}
          >
            <option value="selectT">Select Therapist</option>
            {therapists.map(t => (
              <option key={t.username} value={t.username}>{t.username}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowCalendar(true)}
            title="View Calendar"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 14px',
              border: '1.5px solid #e5e7eb',
              borderRadius: 8,
              background: '#f9fafb',
              color: '#6b7280',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <FaCalendarAlt style={{ fontSize: 14 }} />
            Calendar
          </button>
          <button
            type="button"
            onClick={updateTherapist}
            style={{
              padding: '9px 20px',
              border: '1.5px solid #2563eb',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1d4ed8',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Update Therapist
          </button>
        </div>
      </div>

      {showCalendar && (
        <div className="calendar-overlay">
          <div className="calendar-modal">
            <button className="close-btn" onClick={() => setShowCalendar(false)}>×</button>
            <Cal />
          </div>
        </div>
      )}

      {/* ── Recommendations ── */}
      <div style={card}>
        {cardHeader('📋', 'Recommendations')}
        <div style={{ padding: '12px 18px' }}>
          {recommendations.length > 0 ? (
            <CustomDataTable
              columns={tableData.columns}
              data={tableData.data}
              paginationPerPage={10}
              selectableRows={false}
            />
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No recommendations recorded.</div>
          )}
        </div>
      </div>

      {/* ── Recommended Services — select to send to billing ── */}
      <div style={card}>
        {cardHeader('🛠️', `Recommended Services (${suggestedServices.length})`)}
        <div style={{ padding: '12px 18px' }}>
          {suggestedServices.length > 0 ? (
            <CustomDataTable
              columns={sstableData.columns}
              data={sstableData.data}
              paginationPerPage={10}
              selectableRows={true}
             onSelectedRowsChange={(e) => {
                const selected = e.selectedRows;
                const updatedMods = modSeqs.current.filter(m => selected.some(s => s._uid === m._uid));
                const merged = selected.map(row => {
                  const existing = updatedMods.find(m => m._uid === row._uid);
                  return existing || row;
                });
const newSuggestedServices = suggestedServices.map(row => {
  const stillSelected = selected.some(s => s._uid === row._uid);
  if (!stillSelected) {
    return { ...row, modifiedQtyByPatient: '', remarks: '' };
  }
  return row;
});
setSuggestedServices(newSuggestedServices);
                modSeqs.current = merged;
                setSelectedRowsState(selected);
              }}
              selectableRowSelected={(row) => selectedRowsState.some(r => r._uid === row._uid)}
            />
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No recommended services found. Add services during consultation.</div>
          )}
        </div>
      </div>

      {/* ── Previous GTPs — per-GTP cards in chronological order ── */}
      {gtpPlans.length > 0 && (
        <div>
          <div style={{ padding: '0 18px', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Previous GTPs ({gtpPlans.length})
            </span>
          </div>
          {gtpPlans.map(gtp => {
            const svcs = gtp.suggestedServices || [];
            const boughtList = gtp.bought || [];
            const totalSuggested = svcs.reduce((sum, s) => sum + (Number(s.qty) || 0), 0);
            const totalBought = boughtList.reduce((sum, s) => sum + (Number(s.boughtQty) || Number(s.qty) || 0), 0);
            const isOpen = expandedGtpNumbers.has(gtp.gtpNumber);
            const toggleGtp = () => setExpandedGtpNumbers(prev => {
              const next = new Set(prev);
              next.has(gtp.gtpNumber) ? next.delete(gtp.gtpNumber) : next.add(gtp.gtpNumber);
              return next;
            });
            const isLatest = gtpPlans.indexOf(gtp) === gtpPlans.length - 1;
            const isRejected = gtp.status === 'rejected';
            const statusBg = gtp.status === 'approved' ? '#dcfce7' : isRejected ? '#fef2f2' : '#fef9c3';
            const statusColor = gtp.status === 'approved' ? '#166534' : isRejected ? '#991b1b' : '#854d0e';
            const gtpDate = new Date(gtp.date);
            const dateStr = gtpDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            const timeStr = gtpDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
            const sessLabel = ((gtp.mythsessions || 0) + (gtp.maintsessions || 0) + (gtp.preventivesessions || 0)) || null;

            return (
              <div key={gtp.gtpNumber} style={{
                ...card, marginBottom: 8,
                background: isLatest ? '#f0f9ff' : '#fafbfe',
                border: isLatest ? '1.5px solid #93c5fd' : '1px solid #e5e7eb',
                opacity: isRejected ? 0.6 : 1,
              }}>
                {/* GTP Header — clickable to expand */}
                <div onClick={toggleGtp} style={{
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '10px 18px', borderBottom: isOpen ? '1px solid #e8ecf4' : 'none',
                }}>
                  <span style={{ fontSize: 13, color: '#38bdf8', flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#334155', flexShrink: 0 }}>
                    GTP {gtp.gtpNumber}{isLatest ? ' (Current)' : ''}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {dateStr} {timeStr}
                    {gtp.submittedBy ? ` · ${gtp.submittedBy}` : ''}
                  </span>
                  {sessLabel > 0 && (
                    <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>
                      Doctor:{gtp.mythsessions || 0} Sr.Therapist:{gtp.maintsessions || 0} Therapist:{gtp.preventivesessions || 0}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: '#64748b', marginLeft: 'auto', flexShrink: 0 }}>
                    {totalBought}/{totalSuggested} bought
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: statusBg, color: statusColor, flexShrink: 0,
                    textDecoration: isRejected ? 'line-through' : 'none',
                  }}>{gtp.status}</span>
                </div>
                {/* Expanded detail — full service table with bought status */}
                {isOpen && (
                  <div style={{ padding: '8px 18px 12px' }}>
                    {svcs.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={{ padding: '5px 8px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Service</th>
                            <th style={{ padding: '5px 8px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>Suggested Qty</th>
                            <th style={{ padding: '5px 8px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>Bought Qty</th>
                            <th style={{ padding: '5px 8px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>Amount</th>
                            <th style={{ padding: '5px 8px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {svcs.map((s, i) => {
                            const boughtItem = boughtList.find(b => (b.name || '').toLowerCase() === (s.name || '').toLowerCase());
                            const bQty = boughtItem ? (Number(boughtItem.boughtQty) || Number(boughtItem.qty) || 0) : 0;
                            const sQty = Number(s.qty) || 0;
                            const isFull = bQty >= sQty && sQty > 0;
                            const isPartial = bQty > 0 && bQty < sQty;
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '4px 8px', color: '#475569' }}>{s.name}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'center', color: '#475569' }}>{sQty > 0 ? sQty : '—'}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'center', color: bQty > 0 ? '#1565c0' : '#94a3b8', fontWeight: bQty > 0 ? 700 : 400 }}>{bQty > 0 ? bQty : '0'}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'center', color: '#475569' }}>{s.amount ? `₹${s.amount}` : '—'}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                  {isFull && <span style={{ fontSize: 10, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>Bought</span>}
                                  {isPartial && <span style={{ fontSize: 10, fontWeight: 600, color: '#d97706', background: '#fef9c3', padding: '1px 6px', borderRadius: 4 }}>{bQty}/{sQty}</span>}
                                  {!isFull && !isPartial && sQty > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '1px 6px', borderRadius: 4 }}>Unbought</span>}
                                  {sQty === 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No services in this GTP (exercises only)</div>
                    )}
                    {/* Bill summary for this GTP window */}
                    {(gtp.gtpBills || []).length > 0 && (
                      <div style={{ marginTop: 8, fontSize: 10, color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
                        <span style={{ fontWeight: 600 }}>Bills in this period:</span>{' '}
                        {(gtp.gtpBills || []).map((b, bi) => (
                          <span key={bi} style={{ marginRight: 8 }}>
                            {b.name} ×{b.qty}{b._billing?.invoicedate ? ` (${new Date(b._billing.invoicedate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Action Button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 16 }}>
        <button
          type="button"
          onClick={updateSeqs}
          style={{
            padding: '11px 32px',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.02em',
            boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
          }}
        >
          Go To Billing →
        </button>
      </div>
    </div>
  );
};

export default PostConsultation;
